import { useState, useEffect } from 'react';
import api from '../../lib/api';

interface RoadmapTask {
  id: string;
  description: string;
  status: string;
  priority: number;
  scheduledFor: string;
  completedAt: string | null;
  input: {
    title?: string;
    executionType?: string;
  };
  output: {
    finalText?: string;
  } | null;
}

interface Props {
  mundusId: string;
}

const STATUS_ICONS: Record<string, string> = {
  COMPLETED: '✅',
  IN_PROGRESS: '⏳',
  PENDING: '🔜',
  AWAITING_INPUT: '❓',
  FAILED: '❌',
};

export default function AtentoTaskBoard({ mundusId }: Props) {
  const [tasks, setTasks] = useState<RoadmapTask[]>([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    loadTasks();
  }, [mundusId]);

  const loadTasks = async () => {
    try {
      // Load today's tasks first, then full roadmap stats
      const [todayRes, roadmapRes] = await Promise.all([
        api.get(`/api/mundus/${mundusId}/roadmap/today`),
        api.get(`/api/mundus/${mundusId}/roadmap`),
      ]);
      setTasks(todayRes.data.tasks || []);
      setStats(roadmapRes.data.stats || { total: 0, completed: 0, pending: 0 });
    } catch {
      // No roadmap yet
    } finally {
      setLoading(false);
    }
  };

  if (loading) return null;
  if (stats.total === 0) return null; // No roadmap created yet

  const getTitle = (task: RoadmapTask) => task.input?.title || task.description?.split('\n')[0] || 'Task';
  const completedToday = tasks.filter(t => t.status === 'COMPLETED');
  const progressPercent = stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header with overall progress */}
      <div className="px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold">Atento's Work</h3>
            <p className="text-xs text-blue-100">Day {Math.min(stats.completed + stats.pending, stats.total)} of {stats.total}</p>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold">{progressPercent}%</div>
            <div className="text-xs text-blue-100">{stats.completed}/{stats.total} tasks</div>
          </div>
        </div>
        {/* Progress bar */}
        <div className="w-full bg-blue-400/30 rounded-full h-1.5 mt-2">
          <div
            className="h-1.5 rounded-full bg-white transition-all"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Today's tasks */}
      <div className="p-4">
        {tasks.length > 0 ? (
          <>
            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">
              Today ({completedToday.length}/{tasks.length} done)
            </p>
            <div className="space-y-2">
              {tasks.map((task) => (
                <div
                  key={task.id}
                  className={`flex items-start gap-2 text-sm ${
                    task.status === 'COMPLETED' ? 'opacity-60' : ''
                  }`}
                >
                  <span className="text-base flex-shrink-0 mt-0.5">
                    {STATUS_ICONS[task.status] || '📋'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-slate-800 ${task.status === 'COMPLETED' ? 'line-through' : 'font-medium'}`}>
                      {getTitle(task)}
                    </p>
                    {task.status === 'AWAITING_INPUT' && (
                      <p className="text-xs text-amber-600 mt-0.5">Needs your input</p>
                    )}
                    {task.status === 'COMPLETED' && task.output?.finalText && (
                      <p className="text-xs text-slate-500 mt-0.5 truncate">
                        {task.output.finalText.substring(0, 80)}...
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="text-sm text-slate-500">No tasks scheduled for today.</p>
        )}

        {stats.total > 0 && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="mt-3 text-xs text-blue-600 hover:text-blue-700 font-medium"
          >
            {showAll ? 'Hide full roadmap' : `View full roadmap (${stats.total} tasks) →`}
          </button>
        )}
      </div>
    </div>
  );
}
